import { Component, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import SmoothScroll from "@/components/SmoothScroll";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileActionBar from "@/components/MobileActionBar";
import BackToTop from "@/components/BackToTop";
import Home from "@/pages/Home";
import ClientCentre from "@/pages/ClientCentre";
import ArticlePage from "@/pages/ArticlePage";
import InsightsHub from "@/pages/InsightsHub";
import BusinessAdvisory from "@/pages/BusinessAdvisory";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-[#FFFFFF] px-6 text-center">
          <p className="font-display text-4xl text-[#102036]">Something went wrong.</p>
          <button
            data-testid="error-reload-button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-full bg-[#102036] px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#102036]"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const normalizeOldSlug = (raw) =>
  decodeURIComponent(raw)
    .replace(/[\u2019']/g, "")
    .replace(/\u2026-/g, "-")
    .replace(/\u2026/g, "")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[\u201C\u201D]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const BlogPostRedirect = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/insights/${normalizeOldSlug(slug)}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return null;
};

const LegacyRedirect = ({ to, hash = "" }) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to === "/" ? { pathname: "/", hash } : to, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

function App() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <ErrorBoundary>
      <SmoothScroll>
        {!isAdmin && <Header />}
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/client-centre" element={<ClientCentre />} />
            <Route path="/insights" element={<InsightsHub />} />
            <Route path="/insights/:slug" element={<ArticlePage />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="/services" element={<LegacyRedirect to="/" hash="#services" />} />
            <Route path="/about" element={<LegacyRedirect to="/" hash="#about" />} />
            <Route path="/business-advisory" element={<BusinessAdvisory />} />
            <Route path="/contact-us" element={<LegacyRedirect to="/" hash="#contact" />} />
            <Route path="/tax-center" element={<LegacyRedirect to="/client-centre" />} />
            <Route path="/tax-centre" element={<LegacyRedirect to="/client-centre" />} />
            <Route path="/resources" element={<LegacyRedirect to="/client-centre" />} />
            <Route path="/payments" element={<LegacyRedirect to="/client-centre" />} />
            <Route path="/blog" element={<LegacyRedirect to="/" hash="#insights" />} />
            <Route path="/blog/f/:slug" element={<BlogPostRedirect />} />
            <Route path="/blog/:slug" element={<LegacyRedirect to="/" hash="#insights" />} />
            <Route path="/faq" element={<LegacyRedirect to="/" hash="#services" />} />
            <Route path="*" element={<LegacyRedirect to="/" />} />
          </Routes>
        </main>
        {!isAdmin && <Footer />}
        {!isAdmin && <MobileActionBar />}
        {!isAdmin && <BackToTop />}
        <Toaster position="bottom-center" richColors />
      </SmoothScroll>
    </ErrorBoundary>
  );
}

export default App;
